import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { authAPI } from '../../services/api';
import Button from '../ui/Button';
import FormField, { Input } from '../ui/FormField';
import StatusBanner from '../ui/StatusBanner';
import {
  LockClosedIcon,
  ArrowLeftIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { apiErrorMessage } from '../../utils/apiError';

/**
 * Reset Password Page
 *
 * Allows users to set a new password using a valid reset token.
 * Token is passed via URL query parameter: /reset-password?token=xxx
 */
const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isValidating, setIsValidating] = useState(true);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Password strength indicators
  const passwordStrength = {
    hasMinLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
  };

  const isPasswordValid = Object.values(passwordStrength).every(Boolean);
  const doPasswordsMatch = password === confirmPassword && password.length > 0;

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setIsTokenValid(false);
        setIsValidating(false);
        return;
      }

      try {
        const response = await authAPI.validateResetToken(token);
        setIsTokenValid(response.valid);
      } catch (err) {
        setIsTokenValid(false);
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!isPasswordValid) {
      setError('Please ensure your password meets all requirements.');
      return;
    }

    if (!doPasswordsMatch) {
      setError('Passwords do not match.');
      return;
    }

    setIsSubmitting(true);

    try {
      await authAPI.resetPassword(token, password);
      setIsSuccess(true);
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: any) {
      if (err.response?.status === 400) {
        setError(apiErrorMessage(err, 'Invalid or expired reset link. Please request a new password reset.'));
      } else if (err.response?.status === 429) {
        setError('Too many requests. Please wait a moment and try again.');
      } else {
        setError('An error occurred. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state while validating token
  if (isValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-clinic-cream via-clinic-cream-light to-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-clinic-primary mx-auto mb-4" />
          <p className="text-clinic-muted">Validating reset link...</p>
        </div>
      </div>
    );
  }

  // Invalid token state
  if (!isTokenValid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-clinic-cream via-clinic-cream-light to-white px-6 py-12">
        <div className="absolute top-0 left-0 w-96 h-96 bg-clinic-accent/5 rounded-full -translate-y-1/2 -translate-x-1/2" />

        <div className="relative w-full max-w-md">
          <div className="text-center mb-8">
            <img
              src="/logos/logo-primary.png"
              alt="Clinic Studios"
              className="h-12 w-auto mx-auto"
            />
          </div>

          <div className="bg-white rounded-card shadow-clinic p-8 text-center">
            <div className="bg-clinic-accent/10 rounded-full w-16 h-16 mx-auto mb-6 flex items-center justify-center">
              <ExclamationTriangleIcon className="w-8 h-8 text-clinic-accent" />
            </div>

            <h2 className="text-2xl font-bold text-clinic-dark font-body mb-4">
              Invalid or Expired Link
            </h2>

            <p className="text-clinic-muted mb-6">
              This password reset link is invalid or has expired. Please request a new one.
            </p>

            <Button
              as={Link}
              to="/forgot-password"
              fullWidth
              size="lg"
            >
              Request New Reset Link
            </Button>

            <div className="mt-6">
              <Link
                to="/login"
                className="inline-flex items-center text-sm text-clinic-primary hover:underline"
              >
                <ArrowLeftIcon className="w-4 h-4 mr-1" />
                Back to Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-clinic-cream via-clinic-cream-light to-white px-6 py-12">
        <div className="absolute top-0 left-0 w-96 h-96 bg-clinic-primary/5 rounded-full -translate-y-1/2 -translate-x-1/2" />

        <div className="relative w-full max-w-md">
          <div className="text-center mb-8">
            <img
              src="/logos/logo-primary.png"
              alt="Clinic Studios"
              className="h-12 w-auto mx-auto"
            />
          </div>

          <div className="bg-white rounded-card shadow-clinic p-8 text-center">
            <div className="bg-clinic-primary/10 rounded-full w-16 h-16 mx-auto mb-6 flex items-center justify-center">
              <CheckCircleIcon className="w-8 h-8 text-clinic-primary" />
            </div>

            <h2 className="text-2xl font-bold text-clinic-dark font-body mb-4">
              Password Reset Successful!
            </h2>

            <p className="text-clinic-muted mb-6">
              Your password has been reset. Redirecting to login...
            </p>

            <Link
              to="/login"
              className="text-clinic-primary hover:underline font-medium"
            >
              Go to Login Now
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Reset form
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-clinic-cream via-clinic-cream-light to-white px-6 py-12">
      <div className="absolute top-0 left-0 w-96 h-96 bg-clinic-primary/5 rounded-full -translate-y-1/2 -translate-x-1/2" />
      <div className="absolute bottom-0 right-0 w-72 h-72 bg-clinic-gold/10 rounded-full translate-y-1/2 translate-x-1/2" />

      <div className="relative w-full max-w-md">
        <div className="text-center mb-8">
          <img
            src="/logos/logo-primary.png"
            alt="Clinic Studios"
            className="h-12 w-auto mx-auto"
          />
        </div>

        <div className="bg-white rounded-card shadow-clinic p-8">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-clinic-dark font-body">
              Reset Your Password
            </h2>
            <p className="mt-2 text-clinic-muted">
              Enter your new password below.
            </p>
          </div>

          {error && (
            <div className="mb-6">
              <StatusBanner
                variant="error"
                title="Unable to reset password"
                message={error}
                dismissible
                onDismiss={() => setError(null)}
              />
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <FormField label="New Password" htmlFor="password">
              <Input
                id="password"
                name="password"
                type="password"
                placeholder="Enter new password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                leftIcon={<LockClosedIcon className="h-5 w-5" />}
                required
              />
            </FormField>

            {/* Password strength indicators */}
            {password && (
              <div className="bg-clinic-cream/50 rounded-card p-4">
                <p className="text-sm font-medium text-clinic-dark mb-3">Password must have:</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <span className={`flex items-center ${passwordStrength.hasMinLength ? 'text-clinic-primary' : 'text-clinic-muted'}`}>
                    {passwordStrength.hasMinLength ? (
                      <CheckCircleIcon className="w-4 h-4 mr-2" />
                    ) : (
                      <span className="w-4 h-4 mr-2 rounded-full border border-current flex items-center justify-center text-xs">○</span>
                    )}
                    8+ characters
                  </span>
                  <span className={`flex items-center ${passwordStrength.hasUppercase ? 'text-clinic-primary' : 'text-clinic-muted'}`}>
                    {passwordStrength.hasUppercase ? (
                      <CheckCircleIcon className="w-4 h-4 mr-2" />
                    ) : (
                      <span className="w-4 h-4 mr-2 rounded-full border border-current flex items-center justify-center text-xs">○</span>
                    )}
                    Uppercase letter
                  </span>
                  <span className={`flex items-center ${passwordStrength.hasLowercase ? 'text-clinic-primary' : 'text-clinic-muted'}`}>
                    {passwordStrength.hasLowercase ? (
                      <CheckCircleIcon className="w-4 h-4 mr-2" />
                    ) : (
                      <span className="w-4 h-4 mr-2 rounded-full border border-current flex items-center justify-center text-xs">○</span>
                    )}
                    Lowercase letter
                  </span>
                  <span className={`flex items-center ${passwordStrength.hasNumber ? 'text-clinic-primary' : 'text-clinic-muted'}`}>
                    {passwordStrength.hasNumber ? (
                      <CheckCircleIcon className="w-4 h-4 mr-2" />
                    ) : (
                      <span className="w-4 h-4 mr-2 rounded-full border border-current flex items-center justify-center text-xs">○</span>
                    )}
                    Number
                  </span>
                </div>
              </div>
            )}

            <FormField label="Confirm New Password" htmlFor="confirmPassword">
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                leftIcon={<LockClosedIcon className="h-5 w-5" />}
                error={Boolean(confirmPassword && !doPasswordsMatch)}
                required
              />
            </FormField>

            <Button
              type="submit"
              fullWidth
              size="lg"
              isLoading={isSubmitting}
              disabled={!isPasswordValid || !doPasswordsMatch}
            >
              {isSubmitting ? 'Resetting Password...' : 'Reset Password'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="inline-flex items-center text-sm text-clinic-primary hover:underline"
            >
              <ArrowLeftIcon className="w-4 h-4 mr-1" />
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
